import React, { createContext, useContext, useState, useEffect } from "react";
import { CircularProgress, Box } from "@mui/material";
import {
  initKeycloak,
  isKeycloakAuthenticated,
  getKeycloakUserInfo,
  registerLogoutHandler,
} from "../services/keycloak";
import { logOut } from "../services/auth-service";
import { cacheApi } from "@/services/cache-service";
import { usageMonitoringApi } from "@/services/rag-service";
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const applyThemeModePreference = (themeMode) => {
  if (themeMode !== "dark" && themeMode !== "light") return;

  localStorage.setItem("themeMode", themeMode);
  document.documentElement.classList.remove("light-theme", "dark-theme");
  document.documentElement.classList.add(`${themeMode}-theme`);

  if (themeMode === "light") {
    document.documentElement.classList.remove("dark");
  } else {
    document.documentElement.classList.add("dark");
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [cache, setCache] = useState(undefined);
  // Authentication starts during the mount effect. Keep the application
  // behind the bootstrap loader from the very first render so the frontend
  // fallback theme cannot flash before the selected organization is known.
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [inferenceOnly, setInferenceOnly] = useState(false);
  const [authProvider, setAuthProvider] = useState("keycloak");

  useEffect(() => {
    registerLogoutHandler(logOutUser);
  }, []);

  useEffect(() => {
    // Initialize Keycloak
    setLoading(true);

    try {
      initKeycloak(async () => {
        // This is the callback function passed to initKeycloak

        const authenticated = isKeycloakAuthenticated();

        if (authenticated) {
          const keycloakUser = getKeycloakUserInfo();
          setCurrentUser(keycloakUser);
          setAuthProvider("keycloak");
          handleKeycloakUserProfile(keycloakUser);
          await checkInferenceOnlyStatus(keycloakUser);
          const { data } = await cacheApi.getCache();
          applyThemeModePreference(data?.preferences?.theme_mode);
          if (data?.preferences?.language) {
            setUserProfile((prev) =>
              prev ? { ...prev, language: data.preferences.language } : prev,
            );
          }
          setCache(data);
        } else {
          setCurrentUser(null);
          setUserProfile(null);
          setCache(null);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error("AuthProvider => useEffect => error", error);
    }
  }, [authProvider]);

  // Handle Keycloak user profile
  const handleKeycloakUserProfile = (keycloakUser) => {
    if (!keycloakUser) return;

    try {
      // Create user profile directly from Keycloak data
      const newUserProfile = {
        uid: keycloakUser.id,
        email: keycloakUser.email,
        firstName: keycloakUser.firstName,
        lastName: keycloakUser.lastName,
        username: keycloakUser.username,
        displayName:
          `${keycloakUser.firstName || ""} ${
            keycloakUser.lastName || ""
          }`.trim() || keycloakUser.username,
        photoURL: keycloakUser.photoURL || "",
        emailVerified: keycloakUser.emailVerified,
        roles: keycloakUser.roles || [],
        realm: keycloakUser.realm,
        realmAccess: keycloakUser.realmAccess,
        resourceAccess: keycloakUser.resourceAccess,
        keycloakUser: true,
      };

      // Add any custom attributes from Keycloak if available
      if (keycloakUser.attributes) {
        newUserProfile.attributes = keycloakUser.attributes;

        // Map common attributes to profile fields if they exist
        if (keycloakUser.attributes.organization) {
          newUserProfile.organization = keycloakUser.attributes.organization[0];
        }
        if (keycloakUser.attributes.photoURL) {
          newUserProfile.photoURL = keycloakUser.attributes.photoURL[0];
        }
      }

      setUserProfile(newUserProfile);
    } catch (error) {
      console.error("Error setting up Keycloak user profile:", error);
    }
  };

  // Handle user permissions based on inference key from plans
  const checkInferenceOnlyStatus = async (keycloakUser) => {
    if (!keycloakUser) return;

    try {
      const usagePlansUser = await usageMonitoringApi.getUsagePlansUser();
      if (usagePlansUser) {
        setInferenceOnly(usagePlansUser?.is_inference_only ?? false);
      }
    } catch (error) {
      console.error("Error handling user permissions:", error);
    }
  };

  // Function to refresh user information after direct login
  const refreshUser = () => {
    try {
      if (isKeycloakAuthenticated()) {
        const keycloakUser = getKeycloakUserInfo();
        setCurrentUser(keycloakUser);
        handleKeycloakUserProfile(keycloakUser);
        checkInferenceOnlyStatus(keycloakUser);
        return Promise.resolve(keycloakUser);
      }
      return Promise.resolve(null);
    } catch (error) {
      console.error("Error refreshing user info:", error);
      return Promise.reject(error);
    }
  };

  // Logout function
  const logOutUser = async () => {
    console.log("AuthContext => logOutUser called");
    try {
      // PROMPT: We do NOT clear state locally here anymore.
      // We let Keycloak redirect to /logout-callback which will handle the cleanup.
      // This prevents the app from re-authenticating while redirecting.
      await logOut();
      console.log("Keycloak logout redirect initiated");
    } catch (error) {
      console.error("Error logging out user:", error);
      // Fallback: if Keycloak logout fails, clear manually as a safety measure
      setCurrentUser(null);
      setUserProfile(null);
      setCache(null);
      throw error;
    }
  };

  const value = {
    currentUser,
    cache,
    setCache,
    userProfile,
    setUserProfile,
    loading,
    authProvider,
    logOut: logOutUser,
    refreshUser,
    inferenceOnly,
    setInferenceOnly,
    checkInferenceOnlyStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
